import { DeviceOrderService } from './../../../services/device-order.service';
import { Component, OnInit, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { DeviceOrder } from 'src/app/models';
import { first } from 'rxjs/internal/operators/first';
import { fromEvent } from 'rxjs';
import { map, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { NgbDate, NgbCalendar, NgbDateParserFormatter } from '@ng-bootstrap/ng-bootstrap';
import * as jquery from 'jquery';
import * as moment from 'moment-timezone';
import 'moment/locale/en-nz';
import { Months } from 'src/app/helpers/date-picker/months';

@Component({
  selector: 'app-device-order',
  templateUrl: './device-order.component.html',
  styleUrls: ['./device-order.component.scss']
})
export class DeviceOrderComponent implements OnInit, AfterViewInit {

  @ViewChild('searchDeviceOrderInput') searchDeviceOrderInput: ElementRef;

  deviceOrders: DeviceOrder[] = [];
  currentPage: number;
  lastPage: number;
  pageLinks: number[] = [];
  searchValue = '';

  hoveredDate: NgbDate | null = null;
  fromDate: NgbDate | null;
  toDate: NgbDate | null;
  fromUnixTimestamp = '';
  toUnixTimestamp = '';
  dpFromDate: NgbDate;
  dpToDate: NgbDate;
  modalText = 'Loading...';
  error = false;
  loading = true;

  readonly DELIMITER = '-';

  constructor(
    private pageTitleService: Title,
    private route: ActivatedRoute,
    private deviceOrderService: DeviceOrderService,
    private calendar: NgbCalendar,
    public formatter: NgbDateParserFormatter
  ) { }

  ngOnInit(): void {
    this.setPageTitle();
    this.getDeviceOrders();
  }

  ngAfterViewInit(): void {
    this.search();
  }

  setPageTitle() {
    this.pageTitleService.setTitle(this.route.snapshot.data.title || 'Device Orders');
  }

  onDateFromSelection(fromDate: NgbDate) {
    this.fromDate = fromDate;
    const fromDateF = this.fromDate.day + '/' + this.fromDate.month + '/' + this.fromDate.year;
    if (this.toDate) {
      if (this.fromDate.after(this.toDate)) {
        jquery('#dateInputFrom').val(fromDateF);
        this.fromDate = fromDate;

        this.toDate = null;
        this.toUnixTimestamp = '';
        this.dpToDate = null;
      } else {
        jquery('#dateInputFrom').val(fromDateF);
      }
    }
    this.onDateSelection();
  }

  onDateToSelection(toDate: NgbDate) {
    this.toDate = toDate;
    const toDateF = this.toDate.day + '/' + this.toDate.month + '/' + this.toDate.year;
    if (this.fromDate) {
      if (this.toDate.before(this.fromDate)) {
        jquery('#dateInputFrom').val(toDateF);
        this.fromDate = toDate;

        jquery('#dateInputTo').val('');
        this.toDate = null;
        this.toUnixTimestamp = '';
        this.dpToDate = null;
      } else {
        jquery('#dateInputTo').val(toDateF);
      }
    }
    this.onDateSelection();
  }

  onDateSelection() {
    // Get the Unix Timestamp for DateFrom and DateTo
    if (this.fromDate && this.toDate) {

      // Handles From Date Conversion to timestamp
      const fromMonth = (this.fromDate.month < 10) ? '0' + this.fromDate.month : this.fromDate.month;
      const fromDay = (this.fromDate.day < 10) ? '0' + this.fromDate.day : this.fromDate.day;
      const fromYear = this.fromDate.year;
      const fromDate = fromYear + '-' + fromMonth + '-' + fromDay + ' 00:00:00';
      this.fromUnixTimestamp = moment.tz(fromDate.toString(), 'YYYY-MM-DD HH:mm:ss', moment.tz.guess()).format('X');

      // Handles To Date Conversion to timestamp
      const toMonth = (this.toDate.month < 10) ? '0' + this.toDate.month : this.toDate.month;
      const toDay = (this.toDate.day < 10) ? '0' + this.toDate.day : this.toDate.day;
      const toYear = this.toDate.year;
      const toDate = toYear + '-' + toMonth + '-' + toDay + ' 23:59:59';
      this.toUnixTimestamp = moment.tz(toDate.toString(), 'YYYY-MM-DD HH:mm:ss', moment.tz.guess()).format('X');

    }

    if (this.fromUnixTimestamp !== '' && this.toUnixTimestamp !== '') {
      this.modalText = 'Searching...';
      this.getDeviceOrders(1, this.searchValue, this.fromUnixTimestamp, this.toUnixTimestamp);
    }

  }

  isHovered(date: NgbDate) {
    return this.fromDate && !this.toDate && this.hoveredDate && date.after(this.fromDate) && date.before(this.hoveredDate);
  }

  isInside(date: NgbDate) {
    return this.toDate && date.after(this.fromDate) && date.before(this.toDate);
  }

  isRange(date: NgbDate) {
    return date.equals(this.fromDate) || (this.toDate && date.equals(this.toDate)) || this.isInside(date) || this.isHovered(date);
  }

  validateInput(currentValue: NgbDate | null, input: string): NgbDate | null {
    const parsed = this.formatter.parse(input);
    return parsed && this.calendar.isValid(NgbDate.from(parsed)) ? NgbDate.from(parsed) : currentValue;
  }

  getDeviceOrders(pageNumber: number = 1, searchValue: string = '', dateFrom: string = '', dateTo: string = '') {

    // Show progress modal
    this.showProgressModal();

    this.deviceOrderService.list(pageNumber, searchValue, dateFrom, dateTo)
      .pipe(first())
      .subscribe(
        deviceData => {
          if (deviceData.success) {
            this.deviceOrders = [];
            deviceData.data?.requests.forEach((device: DeviceOrder) => {

              const fullAddress = this.getFullAddress(device.address);

              this.deviceOrders.push({
                id: device.id,
                status: device?.status,
                requested_on: device?.requested_on,
                payment_received: device?.payment_received,
                user: {
                  id: device.user?.id,
                  first_name: device.user?.first_name,
                  last_name: device.user?.last_name,
                  fullname: device.user?.first_name + ' ' + device.user?.last_name
                },
                address: {
                  line1: device.address?.line1,
                  line2: device.address?.line1,
                  suburb: device.address?.suburb,
                  city: device.address?.city,
                  postcode: device.address?.postcode,
                  country: device.address?.country
                },
                fullAddress: `${fullAddress}`
              });
            });
            const totalRecords = deviceData.data.pagination?.total_records;
            this.lastPage = deviceData.data.pagination?.last_page;
            this.pageLinks = [];
            if (totalRecords > 0) {
              this.currentPage = deviceData.data.pagination?.current_page;
              for (let index = 1; index <= this.lastPage; index++) {
                this.pageLinks.push(index);
              }
            }

            setTimeout(() => {
              // hide progress modal
              this.hideProgressModal();
              this.loading = false;
            }, 500);

          }
        },
        error => {
          // hide progress modal
          this.hideProgressModal();
          this.error = true;
        }
      );
  }

  paginate(pageNumber: number) {
    if (pageNumber > 0) {
      this.getDeviceOrders(pageNumber, this.searchValue, this.fromUnixTimestamp, this.toUnixTimestamp);
    }
  }

  search() {
    fromEvent(this.searchDeviceOrderInput.nativeElement, 'keyup')
    .pipe(
      map((event: any) => {
        this.searchValue = event.target.value;
        return event.target.value;
      }),
      debounceTime(2000),
      distinctUntilChanged()
    )
    .subscribe((text: string) => {
      this.modalText = (text !== '') ? 'Searching...' : 'Loading...';
      this.getDeviceOrders(1, text, this.fromUnixTimestamp, this.toUnixTimestamp);
    });
  }

  clearDateFrom() {
    if (this.fromDate) {
      jquery('#dateInputFrom').val('');
      this.fromDate = null;
      this.fromUnixTimestamp = '';
      this.dpFromDate = null;
      this.getDeviceOrders(1, this.searchValue, this.fromUnixTimestamp, this.toUnixTimestamp);
    }
  }

  clearDateTo() {
    if (this.toDate) {
      jquery('#dateInputTo').val('');
      this.toDate = null;
      this.toUnixTimestamp = '';
      this.dpToDate = null;
      this.getDeviceOrders(1, this.searchValue, this.fromUnixTimestamp, this.toUnixTimestamp);
    }
  }

  markDispatched(event: any) {
    const deviceId = event.target.id;
    const deviceStatus = (event.target.checked) ? 'processed' : 'new';

    // Show progress modal
    this.showProgressModal();
    this.modalText = 'Dispatching...';

    this.deviceOrderService.update(deviceId, deviceStatus)
      .pipe(first())
      .subscribe(
        data => {
          setTimeout(() => {
            // hide progress modal
            this.hideProgressModal();
          }, 500);
        },
        error => {
          // hide progress modal
          this.hideProgressModal();
          this.error = true;
        }
      );
  }

  private getFullAddress(address: any) {
    let fullAddress = '';
    // Line 1
    if (address.line1 !== '') {
      fullAddress += address.line1 + ' ';
    }
    // Line 2
    if (address.line2 !== '') {
      fullAddress += address.line2 + ' ';
    }
    // Suburb
    if (address.suburb !== '') {
      fullAddress += address.suburb + ' ';
    }
    // City
    if (address.city !== '') {
      fullAddress += address.city + ' ';
    }
    // Postcode
    if (address.postcode !== '') {
      fullAddress += address.postcode + ' ';
    }
    // Country
    if (address.country !== 0) {
      fullAddress += address.country;
    }
    return fullAddress;
  }

  private showProgressModal() {
    jquery('#loaderSpin').show();
  }

  private hideProgressModal() {
    jquery('#loaderSpin').hide();
  }

}
